import { useCallback, useState } from "react";

import { useTranslation } from "next-i18next";

import Icon from "@components/Icon";
import { useMessage } from "@components/Message";
import scrollToTheTop from "@lib/scrollToTheTop";
import { useAuth } from "@modules/auth";

import {OrderFields, ORDER_MESSAGE_ZONE, ORDER_STORAGE_KEY} from "../../constants";
import useOrderStorage from "../../hooks/storage/useOrderStorage";
import type { TOrderFormData } from "../../models/orderForm";
import createOrUpdateShipment from "../../services/createOrUpdateShipment";
import type { TSaveShipmentReturn } from "../../types";
import PersistentBrowserStorage from "@utils/PersistentBrowserStorage";

type TOrderShipmentReturn = {
  isLoading: boolean;
  saveShipment: (order: TOrderFormData) => Promise<TSaveShipmentReturn>;
};

const useOrderShipment = (): TOrderShipmentReturn => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const setMessage = useMessage();
  const { isLoggedIn } = useAuth();

  const { storeData } = useOrderStorage();

  const saveShipment = useCallback(
    async (order: TOrderFormData) => {
      setIsLoading(true);

      const rawData = PersistentBrowserStorage.getData(ORDER_STORAGE_KEY);
      if (typeof rawData === 'undefined') {
          setIsLoading(false);
          setMessage({
              message: t("order-message-failed_to_create_shipment"),
              props: {
                  variant: "error",
                  icon: <Icon icon="warning" size="1.45rem" />,
                  canClose: true,
                  padding: "0.45rem 0.75rem",
              },
              zone: ORDER_MESSAGE_ZONE,
          });
          scrollToTheTop();
          return { isSuccess: false };
      }

      // If order was initially created by no signed in user and he now is signed in, we need to create new shipment
      // In this case - clearing shipment id here to create new shipment
      const filteredOrder =
        isLoggedIn && !order[OrderFields.SHIPMENT_SIGNED_IN] && order[OrderFields.SHIPMENT_ID]
          ? { ...order, [OrderFields.SHIPMENT_ID]: null }
          : order;

      const result = await createOrUpdateShipment(filteredOrder);

      setIsLoading(false);

      if (!result.isSuccess) {
        setMessage({
          message: result.errorMessage || t("order-message-failed_to_create_shipment"),
          props: {
            variant: "error",
            icon: <Icon icon="warning" size="1.45rem" />,
            canClose: true,
            padding: "0.45rem 0.75rem",
          },
          zone: ORDER_MESSAGE_ZONE,
        });
        scrollToTheTop();
        return { isSuccess: false };
      }

      const shipmentId = result.data?.id;

      const newOrderData = {
        ...filteredOrder,
        [OrderFields.SHIPMENT_ID]: shipmentId,
        [OrderFields.SHIPMENT_SIGNED_IN]: isLoggedIn,
      };
      storeData(newOrderData);

      return { isSuccess: true, shipmentId };
    },
    [isLoggedIn, setMessage, storeData, t]
  );

  return { isLoading, saveShipment };
};
export default useOrderShipment;
